import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useUpdateContractsMutation } from '../../mutations/__generated__/updateContracts.generated';
import { ContractsAsSupplierDocument } from '../../queries/__generated__/contractsAsSupplier.generated';
import { ContractPreview } from '../../hooks/useContracts';
import CompanyLogo from '../../common/components/CompanyLogo';
import { arrayToggle, formatMinimumOrderValue } from '../../common/utils/common.utils';
import { currentContextAtom } from '../../state';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { UserDeliveryZone } from '../../common/models/user.models';

export interface ContractsDraft {
  selected: string[];
}

export interface DeliveryZoneContractsUpdateFormProps {
  onClose: () => void;
  contracts: ContractPreview[];
  deliveryZoneId: string;
}

export default function DeliveryZoneContractsUpdateForm({ onClose, contracts, deliveryZoneId }: DeliveryZoneContractsUpdateFormProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'packaging', 'settings']);
  const { deliveryZones } = useAtomValue(currentContextAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [updateContracts, { data: updateContractsData }] = useUpdateContractsMutation({ refetchQueries: [ContractsAsSupplierDocument] });
  const form = useForm<ContractsDraft>({
    defaultValues: {
      selected: contracts.map(({ id }) => id),
    },
  });
  const selected = form.watch('selected');

  const deliveryZone = useMemo((): UserDeliveryZone => {
    return deliveryZones!.find(({ id }) => deliveryZoneId === id)!;
  }, [deliveryZones, deliveryZoneId]);

  const handleSubmit = useCallback((data: ContractsDraft): void => {
    if (!data.selected) {
      onClose();
      return;
    }

    updateContracts({
      variables: {
        contractIds: selected.map((id) => id),
        contractInput: {
          minimum_order_value: deliveryZone.minimum_order_value,
          minimum_order_value_type: deliveryZone.minimum_order_value_type,
        },
      },
    });
  }, [deliveryZone, selected]);

  const toggleContract = (contractId: string): void => {
    arrayToggle(selected, contractId);
    form.setValue('selected', selected, { shouldDirty: true });
  };

  const handleSelectAll = (): void => {
    if (selected.length === contracts.length) {
      form.setValue('selected', [], { shouldDirty: true });
    } else {
      form.setValue('selected', contracts.map(({ id }) => id));
    }
  };

  useEffect((): void => {
    if (updateContractsData?.update_contract_many) {
      setCommonSnackbarProps({
        label: t('settings:your_profile_has_been_updated'),
        snackbarProps: {
          open: true,
        },
      });
      onClose();
    }
  }, [updateContractsData]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogTitle>
          <Typography>{t('settings:select_your_impacted_customers')}</Typography>
          <Typography variant="bodyMedium">
            {t('settings:your_delivery_zone_update_has_impact')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="bodyLarge">
              {t('settings:all_my_customers')}
            </Typography>
            <Checkbox
              indeterminate={!!selected.length && selected.length < contracts.length}
              onChange={handleSelectAll}
              checked={selected.length === contracts.length}
            />
          </Box>
          <Divider />
          <List>
            {contracts.map(({ id, company, minimum_order_value_type, minimum_order_value }) => {
              const diffLabel = `${t('common:minimum_order_value')}: ${formatMinimumOrderValue(t, minimum_order_value!, minimum_order_value_type)} → ${formatMinimumOrderValue(t, deliveryZone.minimum_order_value!, deliveryZone.minimum_order_value_type)}`;

              return (
                <ListItem
                  key={id}
                  secondaryAction={(
                    <Checkbox
                      checked={selected.includes(id)}
                      onChange={() => toggleContract(id)}
                    />
                  )}
                >
                  <ListItemIcon>
                    <CompanyLogo brand={company.brand} size="medium" variant="transparent" />
                  </ListItemIcon>
                  <ListItemText
                    primary={company.tradeName}
                    secondary={diffLabel}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            {t('settings:none_of_my_customers')}
          </Button>
          <Button type="submit" variant="contained">
            {t('common:validate')}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}
